import React from 'react'
import { useParams } from 'react-router-dom'
import { gql, useQuery } from '@apollo/client'
import Avatar from '@material-ui/core/Avatar'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import Box from '@material-ui/core/Box'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import CardActions from '@material-ui/core/CardActions'
import CardMedia from '@material-ui/core/CardMedia'
import Button from '@material-ui/core/Button'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import { PGA_COM_HOST, MY_PGA_COM_HOST } from 'env'
import useIconAccessor from 'components/lesson-type-category/icons'
import Error from 'components/error'

const GET_ACADEMY_DETAILS = gql`
  query GetAcademyDetails($slug: String) {
    academy(slug: $slug) {
      id
      slug
      name
      description
      logoImageUrl
      featuredImageUrl
      facilities {
        id
        name
        address1
        city
        state
        zip
        phone
        url
      }
      coaches {
        id
        name
        bookingUrl
        profileUrl
        coachProfile {
          profilePhoto
        }
      }
      lessonTypes {
        id
        title
        slug
        durationInMinutes
        priceInCents
        category
        coach {
          id
          name
          bookingUrl
        }
        facilities {
          id
        }
      }
      groupEvents {
        id
        title
        slug
        featuredImageUrl
        startDateTime
      }
    }
  }
`

const CoachCard = ({ coach }) => {
  const coachInitials = coach.name
    .split(' ')
    .map(s => s[0])
    .join('')
    .substring(0, 2)
  return (
    <Card>
      <CardContent>
        <Avatar alt={coach.name} src={coach.coachProfile?.profilePhoto}>
          {coachInitials}
        </Avatar>
        <Typography variant="h5">{coach.name}</Typography>
      </CardContent>
      <CardActions>
        <Button href={coach.profileUrl}>Profile</Button>
        <Button href={coach.bookingUrl}>Schedule</Button>
      </CardActions>
    </Card>
  )
}

const LessonTypeCard = ({ lessonType }) => {
  const accessor = useIconAccessor()
  return (
    <Card>
      <CardContent>
        <Avatar alt={lessonType.category}>
          {accessor[lessonType.category].icon}
        </Avatar>
        <Typography variant="h5">{lessonType.title}</Typography>
        <Typography variant="body1">with {lessonType.coach.name}</Typography>
        <Typography variant="caption">
          {lessonType.durationInMinutes} minutes · $
          {(lessonType.priceInCents / 100).toFixed(2)}
        </Typography>
      </CardContent>
      <CardActions>
        <Button
          href={`${lessonType.coach.bookingUrl}?lessonTypeId=${lessonType.id}&locationId=${lessonType.facilities[0].id}`}
        >
          Book Now
        </Button>
      </CardActions>
    </Card>
  )
}

const GroupEventCard = ({ groupEvent }) => {
  return (
    <Card>
      <CardMedia image={groupEvent.featuredImageUrl} style={{ height: 140 }} />
      <CardContent>
        <Typography variant="h5">{groupEvent.title}</Typography>
        <Typography variant="caption">{groupEvent.startDateTime}</Typography>
      </CardContent>
      <CardActions>
        <Button href={`${PGA_COM_HOST}/things-to-do/events/${groupEvent.slug}`}>
          Details
        </Button>
        <Button
          href={`${MY_PGA_COM_HOST}/things-to-do/events/${groupEvent.slug}/register`}
        >
          Register
        </Button>
      </CardActions>
    </Card>
  )
}

const AcademySchedulePage = () => {
  const { slug } = useParams()
  const { data, loading, error } = useQuery(GET_ACADEMY_DETAILS, {
    variables: { slug },
  })
  const academy = data?.academy

  if (loading) return <LinearProgress />
  if (error) return <Error error={error} />
  if (!academy) return <Error statusCode={404} />

  const facility = academy.facilities[0]

  return (
    <Container>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <img src={academy.featuredImageUrl} alt="" width="100%" />
          <Avatar src={academy.logoImageUrl} />
          <Typography variant="h3">{academy.name}</Typography>
          <Typography variant="body1">
            {facility.address1} {facility.city}, {facility.state} {facility.zip}
          </Typography>
          <Typography variant="body2">{academy.description}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="h4" gutterBottom>
            Coaches
          </Typography>
          {academy.coaches.map(c => (
            <Box mb={2}>
              <CoachCard coach={c} />
            </Box>
          ))}
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="h4" gutterBottom>
            Lesson Types
          </Typography>
          {academy.lessonTypes.map(lt => (
            <Box mb={2}>
              <LessonTypeCard lessonType={lt} />
            </Box>
          ))}
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="h4" gutterBottom>
            Group Events
          </Typography>
          {academy.groupEvents.map(ge => (
            <Box mb={2}>
              <GroupEventCard groupEvent={ge} />
            </Box>
          ))}
        </Grid>
        <Grid item xs={12}>
          <Box height="400px" overflow="scroll" bgcolor="lightgray">
            <pre>{JSON.stringify(academy, null, 2)}</pre>
          </Box>
        </Grid>
      </Grid>
    </Container>
  )
}

export default AcademySchedulePage
